import "mapbox-gl/dist/mapbox-gl.css";

import { Loader2, MapPin } from "lucide-react";
import { useTheme } from "next-themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Layer,
  Map as MapGL,
  type MapRef,
  NavigationControl,
  Popup,
  Source,
} from "react-map-gl/mapbox";
import { toast } from "sonner";
import { useMapboxToken } from "@/hooks/use-mapbox-token";
import type { ScoreFilter } from "./MapLegend";
import { ScoreDetailSidePanel } from "./ScoreDetailSidePanel";
import { SCORE_HEX, SCORE_THRESHOLDS, scoreHex } from "./score-colors";

const LIGHT_STYLE = "mapbox://styles/mapbox/light-v11";
const DARK_STYLE = "mapbox://styles/mapbox/dark-v11";

interface CampaignMapProps {
  campaignId: string;
  profileId: string | null;
  refreshKey: number;
  scoreFilters?: Set<ScoreFilter>;
}

interface ScoreDetailState {
  open: boolean;
  locationId: string;
  addressRaw: string;
  city: string | null;
  floScore: number | null;
  confidence: string | null;
}

const EMPTY_DETAIL: ScoreDetailState = {
  open: false,
  locationId: "",
  addressRaw: "",
  city: null,
  floScore: null,
  confidence: null,
};

export function CampaignMap({ campaignId, refreshKey, scoreFilters }: CampaignMapProps) {
  const mapRef = useRef<MapRef>(null);
  const token = useMapboxToken();
  const { resolvedTheme } = useTheme();
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [scoreDetail, setScoreDetail] = useState<ScoreDetailState>(EMPTY_DETAIL);
  const [hovered, setHovered] = useState<{
    lng: number;
    lat: number;
    addressRaw: string;
    city: string | null;
    floScore: number | null;
    confidence: string | null;
  } | null>(null);

  const mapFilter = useMemo(() => {
    if (!scoreFilters || scoreFilters.size === 0) return undefined;

    // Mapbox GL expression arrays — typed as any since mapbox-gl types are complex
    const conditions: any[] = [];

    if (scoreFilters.has("high")) {
      conditions.push([">=", ["get", "floScore"], SCORE_THRESHOLDS.high]);
    }
    if (scoreFilters.has("medium")) {
      conditions.push([
        "all",
        [">=", ["get", "floScore"], SCORE_THRESHOLDS.medium],
        ["<", ["get", "floScore"], SCORE_THRESHOLDS.high],
      ]);
    }
    if (scoreFilters.has("low")) {
      conditions.push([
        "all",
        ["<", ["get", "floScore"], SCORE_THRESHOLDS.medium],
        ["!=", ["get", "floScore"], null],
      ]);
    }
    if (scoreFilters.has("none")) {
      conditions.push(["==", ["get", "floScore"], null]);
    }

    if (conditions.length === 0) return undefined;
    if (conditions.length === 1) return conditions[0];
    return ["any", ...conditions];
  }, [scoreFilters]);

  const fetchGeoJSON = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/campaigns/${campaignId}/geojson`);
      if (!res.ok) throw new Error(`Failed to fetch map data (${res.status})`);
      const data = await res.json();
      setGeojson(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load map data");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey intentionally triggers re-fetch
  useEffect(() => {
    fetchGeoJSON();
  }, [fetchGeoJSON, refreshKey]);

  // Auto-fit bounds when data loads
  useEffect(() => {
    if (!geojson || geojson.features.length === 0 || !mapRef.current) return;

    const coords = geojson.features.map((f) => (f.geometry as GeoJSON.Point).coordinates);

    // Use a loop instead of Math.min/max spread to avoid call-stack overflow on large datasets
    let minLng = Infinity,
      maxLng = -Infinity,
      minLat = Infinity,
      maxLat = -Infinity;
    for (const [lng, lat] of coords) {
      if (lng! < minLng) minLng = lng!;
      if (lng! > maxLng) maxLng = lng!;
      if (lat! < minLat) minLat = lat!;
      if (lat! > maxLat) maxLat = lat!;
    }

    mapRef.current.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 60, maxZoom: 14, duration: 1000 },
    );
  }, [geojson]);

  const openDetailFromFeature = useCallback((feature: GeoJSON.Feature) => {
    const props = feature.properties ?? {};
    const rawScore = props.floScore;
    const floScore =
      rawScore !== null && rawScore !== undefined && rawScore !== "" ? Number(rawScore) : null;
    setScoreDetail({
      open: true,
      locationId: props.locationId ?? props.id ?? "",
      addressRaw: props.addressRaw ?? "",
      city: props.city ?? null,
      floScore,
      confidence: props.confidence ?? null,
    });
  }, []);

  if (!token) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center space-y-2">
          <MapPin className="h-10 w-10 mx-auto opacity-30" />
          <p>
            {token === null
              ? "Loading map..."
              : "Mapbox token not configured. Add MAPBOX_ACCESS_TOKEN to your .env file."}
          </p>
        </div>
      </div>
    );
  }

  if (loading && !geojson) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center space-y-2">
          <Loader2 className="h-8 w-8 mx-auto animate-spin opacity-40" />
          <p className="text-sm">Loading map data...</p>
        </div>
      </div>
    );
  }

  const mapStyle = resolvedTheme === "dark" ? DARK_STYLE : LIGHT_STYLE;

  return (
    <div className="relative h-full w-full">
      <MapGL
        ref={mapRef}
        mapboxAccessToken={token}
        initialViewState={{
          longitude: -98.5795,
          latitude: 39.8283,
          zoom: 3.5,
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle={mapStyle}
        interactiveLayerIds={["campaign-points"]}
        cursor={hovered ? "pointer" : undefined}
        onClick={(e) => {
          const feature = e.features?.[0];
          if (feature && feature.layer?.id === "campaign-points") {
            openDetailFromFeature(feature as unknown as GeoJSON.Feature);
            return;
          }
          setScoreDetail((prev) => (prev.open ? { ...prev, open: false } : prev));
        }}
        onMouseMove={(e) => {
          const feature = e.features?.[0];
          if (feature && feature.layer?.id === "campaign-points") {
            const props = feature.properties ?? {};
            const raw = props.floScore;
            const floScore = raw !== null && raw !== undefined && raw !== "" ? Number(raw) : null;
            const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
            setHovered({
              lng: lng!,
              lat: lat!,
              addressRaw: props.addressRaw ?? "",
              city: props.city ?? null,
              floScore,
              confidence: props.confidence ?? null,
            });
          } else if (hovered) {
            setHovered(null);
          }
        }}
        onMouseLeave={() => setHovered(null)}
      >
        <NavigationControl position="bottom-right" />

        {geojson && (
          <Source id="campaign-locations" type="geojson" data={geojson}>
            <Layer
              id="campaign-points"
              type="circle"
              {...(mapFilter ? { filter: mapFilter } : {})}
              paint={{
                "circle-color": [
                  "case",
                  ["==", ["get", "floScore"], null],
                  SCORE_HEX.none,
                  [">=", ["get", "floScore"], SCORE_THRESHOLDS.high],
                  SCORE_HEX.high,
                  [">=", ["get", "floScore"], SCORE_THRESHOLDS.medium],
                  SCORE_HEX.medium,
                  SCORE_HEX.low,
                ],
                // Size by score so the high-fliers pop (radius 4 → 10).
                "circle-radius": [
                  "interpolate",
                  ["linear"],
                  ["coalesce", ["get", "floScore"], 0],
                  0,
                  4,
                  100,
                  10,
                ],
                "circle-stroke-color": "#ffffff",
                "circle-stroke-width": 2,
              }}
            />
            {scoreDetail.open && scoreDetail.locationId && (
              <Layer
                id="campaign-points-selected"
                type="circle"
                filter={["==", ["get", "id"], scoreDetail.locationId]}
                paint={{
                  // Yellow scan ring around the selected pin.
                  "circle-color": "rgba(0,0,0,0)",
                  "circle-opacity": 0,
                  "circle-radius": 15,
                  "circle-stroke-color": "#f5c344",
                  "circle-stroke-width": 3,
                  "circle-stroke-opacity": 0.7,
                }}
              />
            )}
          </Source>
        )}

        {hovered && (
          <Popup
            longitude={hovered.lng}
            latitude={hovered.lat}
            anchor="bottom"
            offset={14}
            closeButton={false}
            closeOnClick={false}
            className="flotion-popup"
          >
            <div className="fp-score" style={{ color: scoreHex(hovered.floScore) }}>
              {hovered.floScore == null ? "—" : Math.round(hovered.floScore * 10) / 10}
            </div>
            <div className="fp-addr">{hovered.addressRaw.split(",")[0] || hovered.addressRaw}</div>
            <div className="fp-meta">
              {[hovered.city, hovered.confidence].filter(Boolean).join(" · ")}
            </div>
          </Popup>
        )}
      </MapGL>

      {campaignId && (
        <ScoreDetailSidePanel
          open={scoreDetail.open}
          onClose={() => setScoreDetail((prev) => ({ ...prev, open: false }))}
          campaignId={campaignId}
          locationId={scoreDetail.locationId}
          addressRaw={scoreDetail.addressRaw}
          city={scoreDetail.city}
          floScore={scoreDetail.floScore}
          confidence={scoreDetail.confidence}
        />
      )}
    </div>
  );
}
